import React, { createContext, useCallback, useContext, useMemo, useState } from 'react';
import type { ReactNode } from 'react';

/**
 * Viewer states understood by the widget UI.
 *
 * `verified` is deliberately reserved for ADR-016 S0. This module has no
 * transition that can produce it; adding that producer belongs to the future
 * server-verified identity provider, not a client credential heuristic.
 */
export type ViewerState = 'anonymous' | 'guestHistoryAvailable' | 'verified';

/**
 * Closed evidence vocabulary owned by the My Questions request lifecycle.
 * Callers report server-response evidence, never cookies, URL data, storage,
 * upload proofs, or any other client-held credential.
 */
export type MyQuestionsViewerEvidence =
  | { readonly type: 'request-started' }
  | { readonly type: 'response-received'; readonly data: unknown }
  | { readonly type: 'request-failed' };

type ReportMyQuestionsViewerEvidence = (evidence: MyQuestionsViewerEvidence) => void;

interface ViewerContextValue {
  readonly state: ViewerState;
  readonly reportMyQuestionsEvidence: ReportMyQuestionsViewerEvidence;
}

const reportNoEvidence: ReportMyQuestionsViewerEvidence = () => undefined;

/**
 * Isolated components fail closed when rendered without the widget provider.
 * The reporter is intentionally inert: a child cannot manufacture viewer
 * authority merely because its test or embedding omitted the provider.
 */
const OUTSIDE_PROVIDER_FALLBACK: ViewerContextValue = Object.freeze({
  state: 'anonymous',
  reportMyQuestionsEvidence: reportNoEvidence,
});

const ViewerContext = createContext<ViewerContextValue>(OUTSIDE_PROVIDER_FALLBACK);

function isGuestHistoryAvailable(data: unknown): boolean {
  if (typeof data !== 'object' || data === null || Array.isArray(data)) return false;

  // Use an own-property check so prototype data cannot impersonate a server
  // field. Strict boolean equality rejects strings, numbers and other malformed
  // rollout-skew values. Unrelated/capability-shaped fields are never read.
  return (
    Object.prototype.hasOwnProperty.call(data, 'historyAvailable') &&
    (data as Record<string, unknown>).historyAvailable === true
  );
}

function viewerStateFromEvidence(evidence: MyQuestionsViewerEvidence): ViewerState {
  if (evidence.type !== 'response-received') return 'anonymous';
  return isGuestHistoryAvailable(evidence.data) ? 'guestHistoryAvailable' : 'anonymous';
}

export interface ViewerProviderProps {
  readonly children: ReactNode;
}

/**
 * Widget-local viewer-state owner.
 *
 * Its sole current elevation is the cookie-authenticated My Questions
 * response's own `historyAvailable: true` field. Every other lifecycle event
 * is fail-closed, and no API is exposed for directly assigning a viewer state.
 */
export function ViewerProvider({ children }: ViewerProviderProps): React.ReactElement {
  const [state, setState] = useState<ViewerState>('anonymous');

  const reportMyQuestionsEvidence = useCallback<ReportMyQuestionsViewerEvidence>((evidence) => {
    setState(viewerStateFromEvidence(evidence));
  }, []);

  const value = useMemo<ViewerContextValue>(
    () => ({ state, reportMyQuestionsEvidence }),
    [reportMyQuestionsEvidence, state]
  );

  return <ViewerContext.Provider value={value}>{children}</ViewerContext.Provider>;
}

/** Read-only viewer state for identity-dependent presentation. */
export function useViewerState(): ViewerState {
  return useContext(ViewerContext).state;
}

/**
 * Internal evidence channel for the My Questions request lifecycle.
 *
 * This is exported from the module so that MyQuestions can report its server
 * result, but it is intentionally not part of a package barrel/public API.
 */
export function useMyQuestionsViewerEvidenceReporter(): ReportMyQuestionsViewerEvidence {
  return useContext(ViewerContext).reportMyQuestionsEvidence;
}
